import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import {
  Sparkles,
  Loader2,
  CheckCircle,
  AlertTriangle,
  Lightbulb,
  RefreshCw,
  Wand2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AI Machine Generator
 * 
 * Generate state machines from natural language descriptions using LLM.
 * Features:
 * - Natural language input
 * - Generated machine preview
 * - AI suggestions for improvements
 * - Refinement through conversation
 */

const AIGenerator = ({ open, onOpenChange, onMachineGenerated }) => {
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatedMachine, setGeneratedMachine] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [error, setError] = useState(null);
  const [refinement, setRefinement] = useState('');
  const [showRefinement, setShowRefinement] = useState(false);

  const handleGenerate = async () => {
    if (!description.trim()) {
      toast.error('Please describe your workflow');
      return;
    }

    setLoading(true);
    setError(null);
    setGeneratedMachine(null);
    setSuggestions([]);

    try {
      const response = await fetch(`${API_URL}/api/ai/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ description })
      });

      const data = await response.json();

      if (data.success) {
        setGeneratedMachine(data.machine);
        setSuggestions(data.suggestions || []);
        toast.success('Machine generated successfully!');
      } else {
        setError(data.error || 'Failed to generate machine');
        toast.error(data.error || 'Generation failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      toast.error('Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleRefine = async () => {
    if (!refinement.trim() || !generatedMachine) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/ai/refine`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          machine: generatedMachine,
          refinement: refinement
        })
      });

      const data = await response.json();

      if (data.success) {
        setGeneratedMachine(data.machine);
        setSuggestions(data.suggestions || []);
        setRefinement('');
        setShowRefinement(false);
        toast.success('Machine refined successfully!');
      } else {
        setError(data.error || 'Refinement failed');
        toast.error(data.error || 'Refinement failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      toast.error('Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleUseMachine = () => {
    if (generatedMachine) {
      onMachineGenerated(generatedMachine);
      toast.success('Machine created!');
      onOpenChange(false);
      resetState();
    }
  };

  const resetState = () => {
    setDescription('');
    setGeneratedMachine(null);
    setSuggestions([]);
    setError(null);
    setRefinement('');
    setShowRefinement(false);
  };

  const examplePrompts = [
    "A user authentication flow with login, signup, password reset, and session management",
    "An e-commerce checkout process from cart to payment confirmation",
    "A document approval workflow with draft, review, and publish stages",
    "A subscription lifecycle from free trial to paid to churned"
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) resetState(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] bg-[#0a0e27] border-white/10 p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-white/10">
          <DialogTitle className="flex items-center space-x-2">
            <Sparkles className="w-5 h-5 text-purple-400" />
            <span>AI Machine Generator</span>
            <Badge variant="outline" className="ml-2 text-[10px] text-purple-400 border-purple-500/30">
              Beta
            </Badge>
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Describe your workflow in plain English and let AI generate a state machine for you.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[500px]">
          <div className="p-6 space-y-6">
            {/* Description Input */}
            {!generatedMachine && (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Describe your workflow
                  </label>
                  <Textarea
                    placeholder="Example: A user registration flow where users can sign up with email, verify their email address, and then set up their profile. If verification fails, they can request a new verification email..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="min-h-[120px] bg-black/30 border-white/10"
                    data-testid="ai-description-input"
                  />
                </div>

                {/* Example Prompts */}
                <div>
                  <p className="text-xs text-gray-500 mb-2">Try an example:</p>
                  <div className="flex flex-wrap gap-2">
                    {examplePrompts.map((prompt, i) => (
                      <button
                        key={i}
                        onClick={() => setDescription(prompt)}
                        className="text-xs px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full transition-colors"
                      >
                        {prompt.substring(0, 40)}...
                      </button>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={handleGenerate}
                  disabled={loading || !description.trim()}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  data-testid="ai-generate-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4 mr-2" />
                      Generate Machine
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  <div>
                    <p className="text-sm font-medium text-red-400">Generation Failed</p>
                    <p className="text-xs text-gray-400 mt-1">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Generated Machine Preview */}
            {generatedMachine && (
              <div className="space-y-4">
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-green-400">
                        Machine Generated Successfully
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {generatedMachine.name} with {Object.keys(generatedMachine.states || {}).length} states
                      </p>
                    </div>
                  </div>
                </div>

                {/* States Preview */}
                <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                  <h4 className="text-sm font-medium mb-3">States & Transitions</h4>
                  <div className="space-y-2">
                    {Object.entries(generatedMachine.states || {}).map(([stateName, state]) => (
                      <div key={stateName} className="flex items-start space-x-2">
                        <Badge 
                          variant="outline" 
                          className={cn(
                            "text-[10px] mt-0.5",
                            stateName === generatedMachine.initial 
                              ? "border-green-500/50 text-green-400" 
                              : state.type === 'final'
                                ? "border-red-500/50 text-red-400"
                                : "border-white/30"
                          )}
                        >
                          {stateName === generatedMachine.initial ? 'initial' : state.type}
                        </Badge>
                        <div>
                          <span className="text-sm font-mono">{stateName}</span>
                          {state.transitions?.length > 0 && (
                            <div className="text-xs text-gray-500 mt-0.5">
                              {state.transitions.map(t => (
                                <span key={t.event} className="mr-2">
                                  <span className="text-blue-400">{t.event}</span>
                                  <span className="mx-1">→</span>
                                  <span className="text-green-400">{t.target}</span>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* AI Suggestions */}
                {suggestions.length > 0 && (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <Lightbulb className="w-5 h-5 text-amber-400" />
                      <div>
                        <p className="text-sm font-medium text-amber-400">Suggestions</p>
                        <ul className="text-xs text-gray-400 mt-1 space-y-1">
                          {suggestions.map((suggestion, i) => (
                            <li key={i}>• {suggestion}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Refinement Section */}
                {showRefinement ? (
                  <div className="space-y-3">
                    <Textarea
                      placeholder="Describe what changes you want..."
                      value={refinement}
                      onChange={(e) => setRefinement(e.target.value)}
                      className="min-h-[80px] bg-black/30 border-white/10"
                    />
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowRefinement(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleRefine}
                        disabled={loading || !refinement.trim()}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        {loading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Refine
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowRefinement(true)}
                    className="w-full"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refine with AI
                  </Button>
                )}
              </div>
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="px-6 py-4 border-t border-white/10">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {generatedMachine ? (
            <Button
              onClick={handleUseMachine}
              className="bg-green-600 hover:bg-green-700"
              data-testid="ai-use-machine-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Use This Machine
            </Button>
          ) : (
            <Button
              onClick={handleGenerate}
              disabled={loading || !description.trim()}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AIGenerator;
